import { useEffect, useState, useRef } from "react";
import axios from "axios";
import Abstract_Trans_Fps from "./Abstract_Trans_Fps"
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { getMonthName } from '../../Utils/DateUtils';

function PortabilityJsoTable({ distCode, date, month, onDataLoaded }) {
  const [JSOData, setJSOData] = useState([]);
  const [jsoTitle, setJsoTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState([]);
  const [message, setMessage] = useState("");
  const [renderContent, setRenderContent] = useState(null);
  const nextTableRef = useRef(null);
  const currentTableRef = useRef(null);

  const calculateTotals = (data) => {
    if (!data.length) return setTotals({});
    const totalsObj = data.reduce((totals, row) => {
      Object.entries(row).forEach(([key, value]) => {
        if (key !== "District" && typeof value === "number") {
          totals[key] = (totals[key] || 0) + value;
        }
      });
      return totals;
    }, {});
    setTotals(totalsObj);
  };


  const onJsoClick = (afsoCode) => {
    if (currentTableRef.current) {
      const { bottom } = currentTableRef.current.getBoundingClientRect();
      window.scrollTo({
        top: window.scrollY + bottom + 10,
        behavior: "smooth",
      });
    }
    setRenderContent(
      <div ref={nextTableRef}>
        <Abstract_Trans_Fps afsoCode={afsoCode} distCode={distCode} date={date} month={month}
          onDataLoaded={() => {
            setTimeout(() => {
              if (nextTableRef.current) {
                const top = nextTableRef.current.getBoundingClientRect().top + window.scrollY;
                window.scrollTo({
                  top: top + 10,
                  behavior: "smooth"
                });
              }
            }, 0);
          }}
        />
      </div>
    )

  };


  useEffect(() => {
    if (!distCode) return;
    setRenderContent(null);

    setLoading(true);


    axios.post(`${process.env.REACT_APP_URL_SIDE}/api/DetailedTrans/office`, { date, distCode })
      .then(response => {
        setRenderContent(null);
        setJsoTitle(response.data.header || "JSO Report");
        setJSOData(response.data.data || []);
        if (onDataLoaded) onDataLoaded();
        setMessage(response.data.message || "");
        calculateTotals(response.data.data || []);
      })
      .catch(error => {
        console.error("Error fetching JSO data:", error);
        setJSOData([]);
        setMessage("Internal  error");
      })
      .finally(() => setTimeout(() => setLoading(false), 0));


  }, [distCode, date]);

  const exportToExcel = async () => {
    if (!JSOData.length) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Portability Summary', {
      views: [{ showGridLines: true }]
    });

    // Helper to style each cell in a rectangular range (inclusive)
    const applyStyleToRange = (ws, startRow, startCol, endRow, endCol, styleFn) => {
      for (let r = startRow; r <= endRow; r++) {
        const row = ws.getRow(r);
        for (let c = startCol; c <= endCol; c++) {
          styleFn(row.getCell(c));
        }
      }
    };

    const thinBorder = { style: 'thin', color: { argb: 'FF000000' } };
    const headerFill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFEFEFEF' }
    };

    // ===== Title row (row 1) =====
    worksheet.mergeCells('A1:Y1'); // 25 columns (A..Y)
    const titleCell = worksheet.getCell('A1');
    titleCell.value = jsoTitle || `Detailed Transactions ${date || ''}`;
    titleCell.font = { bold: true, size: 14 };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(1).height = 26;

    // ===== Header rows (rows 2,3,4) - create the content first =====
    // Row2 (top headers)
    worksheet.addRow([
      "Sl.No", "Office", "Total Cards (A)",
      `Date : ${date}`, "", "", "", "", "", "", "", "", "",
      getMonthName(month), "", "", "", "", "", "", "", "", "",
      "Total Availed % (E*100)/A",
      "Partial %(Against Availed Cards)(C*100)/E"
    ]);

    // Row3 (second header line)
    worksheet.addRow([
      "", "", "",
      "Trans", "", "", "", "", "", "Availed Cards", "", "", "",
      "Trans", "", "", "", "", "", "Availed Cards", "", "", "",
      "", ""
    ]);

    // Row4 (third header line)
    worksheet.addRow([
      "", "", "",
      "Biometric", "Aadhar OTP", "Mobile OTP", "Iris", "Partial Offline", "Total",
      "Portability", "Partial Offline", "Online", "Total",
      "Biometric", "Aadhar OTP", "Mobile OTP", "Iris", "Partial Offline", "Total",
      "Portability (B)", "Partial Offline (C)", "Online (D)", "Total (E = C+D)",
      "", ""
    ]);

    // ===== Merge cells to match table's rowspan/colspan =====
    worksheet.mergeCells('A2:A4'); // Sl.No
    worksheet.mergeCells('B2:B4'); // District
    worksheet.mergeCells('C2:C4'); // Total Cards (A)

    worksheet.mergeCells('D2:M2'); // Date : {date} (spans 10 cols)
    worksheet.mergeCells('N2:W2'); // Current Month (spans 10 cols)

    worksheet.mergeCells('X2:X4'); // Total Availed %
    worksheet.mergeCells('Y2:Y4'); // Partial %

    // second row merges for Trans and Availed Cards groups
    worksheet.mergeCells('D3:I3'); // Trans (Date) spans cols D-I
    worksheet.mergeCells('J3:M3'); // Availed Cards (Date) spans cols J-M

    worksheet.mergeCells('N3:S3'); // Trans (Month) spans cols N-S
    worksheet.mergeCells('T3:W3'); // Availed Cards (Month) spans cols T-W

    // ===== Style header area (rows 2..4, cols 1..25) =====
    applyStyleToRange(worksheet, 2, 1, 4, 25, (cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.fill = headerFill;
      cell.border = {
        top: thinBorder,
        left: thinBorder,
        bottom: thinBorder,
        right: thinBorder
      };
    });

    worksheet.getRow(2).height = 20;
    worksheet.getRow(3).height = 18;
    worksheet.getRow(4).height = 18;

    // ===== Data Rows start at row 5 =====
    JSOData.forEach((row, index) => {
      const totalAvailedPerc = row.total_cards ? ((row.avail_cards || 0) * 100 / row.total_cards) : 0;
      const partialPerc = row.avail_cards ? ((row.avail_poff_cards || 0) * 100 / row.avail_cards) : 0;

      const r = worksheet.addRow([
        index + 1,
        row.afso_name_en || "",
        Number(row.total_cards || 0),
        Number(row.avail_bio_trans_today || 0),
        Number(row.avail_otp_trans_today || 0),
        Number(row.avail_motp_trans_today || 0),
        Number(row.avail_iris_trans_today || 0),
        Number(row.avail_poff_trans_today || 0),
        Number(row.avail_trans_today || 0),
        Number(row.avail_port_cards_today || 0),
        Number(row.avail_poff_cards_today || 0),
        Number(row.online_cards_today || 0),
        Number(row.avail_cards_today || 0),
        Number(row.avail_bio_trans || 0),
        Number(row.avail_otp_trans || 0),
        Number(row.avail_motp_trans || 0),
        Number(row.avail_iris_trans || 0),
        Number(row.avail_poff_trans || 0),
        Number(row.avail_trans || 0),
        Number(row.avail_port_cards || 0),
        Number(row.avail_poff_cards || 0),
        Number(row.online_cards || 0),
        Number(row.avail_cards || 0),
        Number(totalAvailedPerc.toFixed(2)),
        Number(partialPerc.toFixed(2))
      ]);

      // style each cell in the data row
      r.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        // text column: District (col 2) align left, others align right/center
        if (colNumber === 2) {
          cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        } else {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }

        // apply numeric format for numeric columns (3..23)
        if (colNumber >= 3 && colNumber <= 23) {
          cell.numFmt = '0.00'; // show two decimals
        }

        cell.border = {
          top: thinBorder,
          left: thinBorder,
          bottom: thinBorder,
          right: thinBorder
        };
      });
    });

    // ===== Totals row =====
    const totalRowIndex = worksheet.lastRow.number + 1;
    const totalRow = worksheet.addRow([
      "Total", "",
      Number(totals.total_cards || 0),
      Number(totals.avail_bio_trans_today || 0),
      Number(totals.avail_otp_trans_today || 0),
      Number(totals.avail_motp_trans_today || 0),
      Number(totals.avail_iris_trans_today || 0),
      Number(totals.avail_poff_trans_today || 0),
      Number(totals.avail_trans_today || 0),
      Number(totals.avail_port_cards_today || 0),
      Number(totals.avail_poff_cards_today || 0),
      Number(totals.online_cards_today || 0),
      Number(totals.avail_cards_today || 0),
      Number(totals.avail_bio_trans || 0),
      Number(totals.avail_otp_trans || 0),
      Number(totals.avail_motp_trans || 0),
      Number(totals.avail_iris_trans || 0),
      Number(totals.avail_poff_trans || 0),
      Number(totals.avail_trans || 0),
      Number(totals.avail_port_cards || 0),
      Number(totals.avail_poff_cards || 0),
      Number(totals.online_cards || 0),
      Number(totals.avail_cards || 0),
      Number(totals.total_cards ? ((totals.avail_cards * 100) / totals.total_cards) : 0),
      Number(totals.avail_cards ? ((totals.avail_poff_cards * 100) / totals.avail_cards) : 0)
    ]);

    worksheet.mergeCells(`A${totalRowIndex}:B${totalRowIndex}`);
    applyStyleToRange(worksheet, totalRowIndex, 1, totalRowIndex, 25, (cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: thinBorder,
        left: thinBorder,
        bottom: thinBorder,
        right: thinBorder
      };
      // Totals numeric columns keep two decimals
      if (cell._column && cell._column.number >= 3) cell.numFmt = '0.00';
    });

    // ===== Column widths (A..Y) =====
    worksheet.columns = [
      { width: 8 },  // A Sl.No
      { width: 22 }, // B Office
      { width: 16 }, // C Total Cards

      { width: 14 }, // D Biometric (Date)
      { width: 14 }, // E Aadhar OTP (Date)
      { width: 14 }, // F Mobile OTP (Date)
      { width: 14 }, // G Iris (Date)
      { width: 16 }, // H Partial Offline (Date)
      { width: 12 }, // I Total (Date)

      { width: 14 }, // J Portability (Date)
      { width: 16 }, // K Partial Offline (Date)
      { width: 12 }, // L Online (Date)
      { width: 12 }, // M Total (Date)

      { width: 14 }, // N Biometric (Month)
      { width: 14 }, // O Aadhar OTP (Month)
      { width: 14 }, // P Mobile OTP (Month)
      { width: 14 }, // Q Iris (Month)
      { width: 16 }, // R Partial Offline (Month)
      { width: 12 }, // S Total (Month)

      { width: 14 }, // T Portability (Month)
      { width: 16 }, // U Partial Offline (Month)
      { width: 12 }, // V Online (Month)
      { width: 14 }, // W Total (Month)

      { width: 18 }, // X Total Availed %
      { width: 20 }  // Y Partial %
    ];

    // ===== Save file =====
    const safeDate = (date || '').toString().replace(/[\/\\: ]+/g, '_') || new Date().toISOString().slice(0, 10);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${jsoTitle}_${safeDate}.xlsx`);
  };



  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Box sx={{ display: 'flex' }}>
      <CircularProgress />
    </Box></div>
  }

  return (
    <div>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Box sx={{ display: 'flex' }}>
          {CircularProgress}
        </Box></div>
      ) : JSOData.length > 0 ? (
        <table align="center" border={1} ref={currentTableRef}>
          <thead>
            <tr className="tableheader" >
              <th colSpan={25}>{jsoTitle}</th>
            </tr>
            <tr className="tableheader" >
              <th rowSpan={3}>Sl.No</th>
              <th rowSpan={3}>Office</th>
              <th rowSpan={3}>Total Cards (A)</th>
              <th colSpan={10}>Date : {date}</th>
              <th colSpan={10}>{getMonthName(month)}</th>
              <th rowSpan={3}>Total Availed % (E*100)/A</th>
              <th rowSpan={3}>Partial %(Against Availed Cards)(C*100)/E</th>
            </tr>
            <tr className="tableheader" >

              <th colSpan={6}>Trans</th>
              <th colSpan={4}>Availed Cards</th>
              <th colSpan={6}>Trans</th>
              <th colSpan={4}>Availed Cards</th>
            </tr>
            <tr className="tableheader">
              <th>Biometric</th>
              <th>Aadhar OTP</th>
              <th>Mobile OTP</th>
              <th>Iris</th>
              <th>Partial Offline</th>
              <th>Total</th>
              <th>Portability</th>
              <th>Partial Offline</th>
              <th>Online</th>
              <th>Total</th>
              <th>Biometric</th>
              <th>Aadhar OTP</th>
              <th>Mobile OTP</th>
              <th>Iris</th>
              <th>Partial Offline</th>
              <th>Total</th>
              <th>Portability (B)</th>
              <th>Partial Offline (C)</th>
              <th>Online (D)</th>
              <th>Total (E = C+D)</th>
            </tr>


          </thead>
          <tbody>
            {JSOData.map((row, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td onClick={() => onJsoClick(row.afso_code)} style={{ cursor: 'pointer', color: 'blue' }}   ><u>{row.afso_name_en}</u></td>
                <td style={{ textAlign: 'right' }} >{row.total_cards || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_bio_trans_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_otp_trans_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_motp_trans_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_iris_trans_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_poff_trans_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_trans_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_port_cards_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_poff_cards_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.online_cards_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_cards_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_bio_trans || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_otp_trans || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_motp_trans || "0"}</td >
                <td style={{ textAlign: 'right' }} >{row.avail_iris_trans || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_poff_trans || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_trans || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_port_cards || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_poff_cards || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.online_cards || "0"}</td>
                <td style={{ textAlign: 'right' }} >{row.avail_cards || "0"}</td>
                <td style={{ textAlign: 'right' }} >{Number(row.total_availed || "0").toFixed(2)}</td>
                <td style={{ textAlign: 'right' }} >{Number(row.partial || "0").toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="tableheader" >
              <th colSpan={2}>Total</th>
              <th style={{ textAlign: 'right' }} >{totals.total_cards || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_bio_trans_today || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_otp_trans_today || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_motp_trans_today || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_iris_trans_today || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_poff_trans_today || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_trans_today || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_port_cards_today || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_poff_cards_today || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.online_cards_today || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_cards_today || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_bio_trans || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_otp_trans || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_motp_trans || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_iris_trans || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_poff_trans || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_trans || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_port_cards || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_poff_cards || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.online_cards || "0"}</th>
              <th style={{ textAlign: 'right' }} >{totals.avail_cards || "0"}</th>
              <th style={{ textAlign: 'right' }} >{Number(totals.avail_cards * 100 / totals.total_cards || "0").toFixed(2)}</th>
              <th style={{ textAlign: 'right' }} >{Number(totals.avail_poff_cards * 100 / totals.avail_cards || "0").toFixed(2)}</th>
            </tr>
            <tr align="center"  >
              <td colSpan={25}>
                <button className="button button2" onClick={exportToExcel} >Export to Excel</button>
              </td>
            </tr>
          </tfoot>
        </table>
      ) : (
        <p>
          <div class="mainhead" align="center">{message}</div>
        </p>
      )}
      <br /><br />




      {renderContent}

    </div>
  );
}

export default PortabilityJsoTable;
